import React, {
  useState,
  useEffect,
  FunctionComponent,
  CSSProperties,
  useRef,
} from "react";
import { DropdownItem } from "./DropdownItem";
import { SearchIcon } from "../../../assets/icons";
import { useDebounce } from "../../../hooks/useDebounce";

interface SearchInputProps<T> {
  jsonFileUrl?: string;
  data: Array<T>;
  displayKey: keyof T;
  onSelect?: (item: T | null) => void;
  isLoading?: boolean;
  searchedDone?: boolean;
  setSearchedDone?: (searchedDone: boolean) => void;
  placeholder?: string;
  classNames?: string;
  value?: string;
  selected?: T | null;
  handleChangeText?: (text: string) => void;
  style?: React.CSSProperties;
  inputStyleMain?: React.CSSProperties;
  image?: FunctionComponent<{ className?: string }>;
  clearIcon?: FunctionComponent<{ color?: string }>;
  emptyMessage?: string;
  error?: string;
  onClearError?: () => void;
  dropdownStyle?: React.CSSProperties;
  dropdownClassName?: string;
  clearIconClass?: string;
  emptyLinks?: Array<{
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  }>;
  emptyLinkStyle?: CSSProperties;
  specificSelectedValue?: boolean;
  icon?: React.ReactNode;
  searchKey: keyof T | Array<keyof T>;
  dropdownWidth?: string;
  expenseDropDown?: boolean;
  onSearch?: (searchTerm: string) => void;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  searchText: string;
  handleClearSelection: () => void;
}

const TableSearchInput = <T extends { [key: string]: any }>({
  onSelect,
  isLoading = false,
  searchedDone,
  setSearchedDone,
  placeholder,
  classNames,
  selected,
  handleChangeText,
  style,
  clearIcon: ClearIcon,
  onClearError,
  clearIconClass,
  icon,
  inputStyleMain,
  onSearch,
  searchText,
  setSearchText,
  handleClearSelection,
}: SearchInputProps<T>) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const debouncedQuery = useDebounce(searchText, 500);

  useEffect(() => {
    if (debouncedQuery.length >= 3) {
      onSearch?.(debouncedQuery);
      setSearchedDone?.(false);
    }
  }, [debouncedQuery]);

  // Handle changes in the input
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    setSearchText(val);
    onClearError && onClearError();
    setSearchedDone && setSearchedDone(false);
  };

  const handleInputClick = () => {
    // If we want to show suggestions on focus (even if empty), we could do so here
    // For now, only show if there's already text
    if (searchText.trim().length > 0) {
      setShowDropdown(true);
    }
    onClearError && onClearError();
  };
  const inputClassName = `${
    classNames ||
    "border border-[#138EFF] md:h-[48px] h-[48px]  rounded-md lg:placeholder:text-[16px] placeholder:text-[14px] lg:p-2 lg:mt-[5px] lg:leading-normal"
  } focus:outline-none w-full focus:shadow-outline appearance-none focus:ring-0 ${
    selected ? "pl-5 border-[#138EFF]" : ""
  }`;

  const inputStyle = selected ? { backgroundColor: "#E3EFFC" } : {};
  return (
    <div className="relative w-full" style={style}>
      <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        {icon ? icon : <SearchIcon className="text-gray-500" />}
      </span>
      <input
        type="text"
        placeholder={placeholder}
        value={searchText}
        onChange={handleInputChange}
        className={`${icon ? "pl-[2.25em]" : ""} ${inputClassName}`}
        disabled={Boolean(selected)}
        onClick={handleInputClick}
        ref={inputRef}
        style={{ ...inputStyle, ...inputStyleMain }}
      />
      {isLoading && (
        <div className="absolute inset-y-0 right-6 flex items-center">
          <div className="w-5 h-5 border-2 border-gray-200 border-t-[#044E97] rounded-full animate-spin"></div>
        </div>
      )}
      {!isLoading && searchText && (
        <button
          onClick={handleClearSelection}
          className={`absolute inset-y-0 right-6 flex items-center ${clearIconClass}`}
        >
          {ClearIcon ? <ClearIcon color="#212121" /> : "x"}
        </button>
      )}
    </div>
  );
};

export { TableSearchInput };
